// GENERATED by @platform/contracts from specs/billing/main.tsp. Do not edit; run `pnpm generate`.
/* eslint-disable */
import { z } from 'zod';
/** A path inside the tenant's app (never a host): `/settings/billing`. */
export const AppPath = z
    .string()
    .regex(/^\/[A-Za-z0-9._~!$&'()*+,;=:@%\/-]*$/)
    .max(512);
/** How the package is priced: one Stripe price × seat quantity (org) or one flat price (solo). */
export const BillingModel = z.enum(['perSeat', 'flat']);
/** Subscription lifecycle as billing tracks it (Stripe statuses, plus `none` before checkout). */
export const SubscriptionStatus = z.enum([
    'none',
    'incomplete',
    'trialing',
    'active',
    'past_due',
    'unpaid',
    'cancelled',
    'paused',
]);
/** Package identifier (`pkg_...`), as the platform catalog defines it. */
export const PackageId = z.string().regex(/^pkg_[a-z0-9-]+$/);
/** Seats: what the subscription is billed for and how many memberships exist. */
export const SeatCount = z.object({ licensed: z.int32().min(0), used: z.int32().min(0) });
/** ISO 4217 currency code, lower-case as Stripe returns it. */
export const Currency = z.string().regex(/^[a-z]{3}$/);
/** The invoice Stripe will issue next. */
export const UpcomingInvoice = z.object({
    amountDueCents: z.int32().min(0),
    currency: Currency,
    dueAt: z.iso.datetime({ offset: true }).optional(),
});
/** Stripe invoice identifier (`in_...`). */
export const StripeInvoiceId = z.string().regex(/^in_[A-Za-z0-9]+$/);
/** Lifecycle of an invoice as Stripe reports it. */
export const InvoiceStatus = z.enum(['draft', 'open', 'paid', 'uncollectible', 'void']);
/** One invoice, as much as a tenant admin needs to see. */
export const InvoiceSummary = z.object({
    invoiceId: StripeInvoiceId,
    status: InvoiceStatus,
    amountDueCents: z.int32().min(0),
    amountPaidCents: z.int32().min(0),
    currency: Currency,
    periodStart: z.iso.datetime({ offset: true }).optional(),
    periodEnd: z.iso.datetime({ offset: true }).optional(),
    hostedInvoiceUrl: z.url().optional(),
    paidAt: z.iso.datetime({ offset: true }).optional(),
    attemptCount: z.int32().min(0),
    nextPaymentAttemptAt: z.iso.datetime({ offset: true }).optional(),
});
/** Stripe Checkout Session identifier (`cs_...`, `cs_test_...`). */
export const StripeCheckoutSessionId = z.string().regex(/^cs_[A-Za-z0-9_]+$/);
/** A Checkout Session that is still open for this tenant. */
export const PendingCheckout = z.object({
    sessionId: StripeCheckoutSessionId,
    checkoutUrl: z.url(),
    expiresAt: z.iso.datetime({ offset: true }),
});
/** The tenant's billing state in one read. Powers the billing settings page. */
export const BillingSummary = z.object({
    status: SubscriptionStatus,
    packageId: PackageId.optional(),
    billingModel: BillingModel,
    seats: SeatCount,
    currentPeriodStart: z.iso.datetime({ offset: true }).optional(),
    currentPeriodEnd: z.iso.datetime({ offset: true }).optional(),
    cancelAtPeriodEnd: z.boolean(),
    nextInvoice: UpcomingInvoice.optional(),
    lastInvoice: InvoiceSummary.optional(),
    portalAvailable: z.boolean(),
    pendingCheckout: PendingCheckout.optional(),
});
/**
 * Start (or resume) Checkout for the caller's tenant. The package and price come from the
 * platform (`platform.tenant.checkoutRequested`); `packageId`, when given, must match it —
 * a different package is adopted through the platform first, never here.
 */
export const CheckoutRequest = z.object({
    packageId: PackageId.optional(),
    seats: z.int32().min(1).max(1000).optional(),
    successPath: AppPath.optional(),
    cancelPath: AppPath.optional(),
});
/** A hosted Checkout Session the browser is redirected to. */
export const CheckoutSession = z.object({
    sessionId: StripeCheckoutSessionId,
    checkoutUrl: z.url(),
    expiresAt: z.iso.datetime({ offset: true }),
});
export const PortalRequest = z.object({ returnPath: AppPath.optional() });
/** A Stripe Customer Portal session (card on file, invoices, cancel). */
export const PortalSession = z.object({ portalUrl: z.url() });
/** Machine-readable error codes. Never say whether a resource exists to a caller outside its tenant. */
export const ErrorCode = z.enum([
    'validation',
    'unauthenticated',
    'forbidden',
    'notFound',
    'conflict',
    'rateLimited',
    'internal',
]);
export const ErrorDetail = z.object({ path: z.string().optional(), message: z.string() });
/** The one error shape. Sent with the matching HTTP status. */
export const ErrorResponse = z.object({
    code: ErrorCode,
    message: z.string(),
    requestId: z.string(),
    details: z.array(ErrorDetail).optional(),
});
/** Tenant identifier. The control plane's own resources use the literal `platform`. */
export const TenantId = z.string().regex(/^[a-z0-9][a-z0-9-]{1,62}$/);
/** Stripe customer identifier (`cus_...`). */
export const StripeCustomerId = z.string().regex(/^cus_[A-Za-z0-9]+$/);
/**
 * A Stripe event as delivered to the webhook. Only `id` and `type` are validated here; the
 * handler verifies the signature over the raw body BEFORE anything is parsed, then reads
 * `data.object` per event type. Additional properties are Stripe's.
 */
export const StripeEvent = z
    .object({ id: z.string().regex(/^evt_[A-Za-z0-9]+$/), type: z.string() })
    .catchall(z.unknown());
export const StripeMode = z.enum(['sandbox', 'live']);
/** Stripe price identifier (`price_...`). */
export const StripePriceId = z.string().regex(/^price_[A-Za-z0-9]+$/);
/** Stripe subscription identifier (`sub_...`). */
export const StripeSubscriptionId = z.string().regex(/^sub_[A-Za-z0-9]+$/);
/** An operator adjustment. Every field is optional but the note; the change is audited. */
export const SubscriptionAdjust = z.object({
    seats: z.int32().min(1).max(1000).optional(),
    cancelAtPeriodEnd: z.boolean().optional(),
    note: z.string().min(3).max(500),
});
/** A tenant's subscription as platform staff see it. Stripe ids only; no card data, no PII. */
export const SubscriptionRecord = z.object({
    tenantId: TenantId,
    status: SubscriptionStatus,
    packageId: PackageId.optional(),
    stripePriceId: StripePriceId.optional(),
    stripeCustomerId: StripeCustomerId.optional(),
    stripeSubscriptionId: StripeSubscriptionId.optional(),
    billingModel: BillingModel,
    seats: SeatCount,
    currentPeriodStart: z.iso.datetime({ offset: true }).optional(),
    currentPeriodEnd: z.iso.datetime({ offset: true }).optional(),
    cancelAtPeriodEnd: z.boolean(),
    mode: StripeMode,
    createdAt: z.iso.datetime({ offset: true }),
    updatedAt: z.iso.datetime({ offset: true }),
});
export const WebhookAck = z.object({
    received: z.boolean(),
    eventId: z.string(),
    duplicate: z.boolean(),
});
/** One validator set per operation, keyed by operationId. Same order as the route manifest. */
export const operations = {
    Subscriptions_list: {
        operationId: 'Subscriptions_list',
        method: 'GET',
        path: '/admin/subscriptions',
        params: z.object({}),
        query: z.object({
            cursor: z.string().optional(),
            limit: z.int32().min(1).max(200).default(50),
            status: SubscriptionStatus.optional(),
        }),
        responses: {
            '200': z.object({
                tenantId: TenantId,
                requestId: z.string(),
                items: z.array(SubscriptionRecord),
                nextCursor: z.string().optional(),
            }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Subscriptions_get: {
        operationId: 'Subscriptions_get',
        method: 'GET',
        path: '/admin/subscriptions/{tenantId}',
        params: z.object({ tenantId: TenantId }),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: SubscriptionRecord }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Subscriptions_adjust: {
        operationId: 'Subscriptions_adjust',
        method: 'POST',
        path: '/admin/subscriptions/{tenantId}/adjust',
        params: z.object({ tenantId: TenantId }),
        query: z.object({}),
        body: SubscriptionAdjust,
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: SubscriptionRecord }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Checkouts_create: {
        operationId: 'Checkouts_create',
        method: 'POST',
        path: '/checkout',
        params: z.object({}),
        query: z.object({}),
        body: CheckoutRequest,
        responses: {
            '201': z.object({ tenantId: TenantId, requestId: z.string(), data: CheckoutSession }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Portals_create: {
        operationId: 'Portals_create',
        method: 'POST',
        path: '/portal',
        params: z.object({}),
        query: z.object({}),
        body: PortalRequest,
        responses: {
            '201': z.object({ tenantId: TenantId, requestId: z.string(), data: PortalSession }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
            '409': ErrorResponse,
        },
    },
    Summary_get: {
        operationId: 'Summary_get',
        method: 'GET',
        path: '/summary',
        params: z.object({}),
        query: z.object({}),
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: BillingSummary }),
            '400': ErrorResponse,
            '401': ErrorResponse,
            '403': ErrorResponse,
            '404': ErrorResponse,
        },
    },
    Webhooks_receive: {
        operationId: 'Webhooks_receive',
        method: 'POST',
        path: '/webhook',
        params: z.object({}),
        query: z.object({}),
        body: StripeEvent,
        responses: {
            '200': z.object({ tenantId: TenantId, requestId: z.string(), data: WebhookAck }),
            '400': ErrorResponse,
            '401': ErrorResponse,
        },
    },
};
//# sourceMappingURL=schemas.js.map